import { Star, ExternalLink } from "lucide-react";
import { GOOGLE_REVIEWS, GOOGLE_PROFILE_URL } from "@/data/googleReviews";
import Reveal from "@/components/site/Reveal";

/**
 * Editorial Testimonials — no cards, no sliders.
 * Massive Serif pull-quotes with hanging punctuation, author as small circular
 * element floating at the end of the quote. Sits on a warm cream background
 * (not the harsh saturated brand-rose block).
 *
 * We highlight 3 signature reviews as editorial pull-quotes;
 * the Google summary + link to all remain as tactile trust anchors.
 */

function GoogleG({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z" />
    </svg>
  );
}

function Stars({ size = "h-3.5 w-3.5" }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, idx) => (
        <Star
          key={idx}
          className={`${size} fill-[#FBBC04] text-[#FBBC04]`}
          strokeWidth={1}
        />
      ))}
    </div>
  );
}

// Feature-Reihenfolge: eine kürzere Emotions-Quote, eine reflektierte, eine emphatische
const FEATURED_IDS = ["dominik-g", "iwin-h", "anna-c"];

function PullQuote({ review, index, sideMode }) {
  // Side alignment: 0=left-heavy, 1=right-heavy, 2=center-slight-left
  const alignments = [
    "lg:ml-0 lg:mr-auto",
    "lg:ml-auto lg:mr-0 lg:text-right",
    "lg:mx-auto",
  ];
  const align = alignments[sideMode] || alignments[0];

  return (
    <Reveal delay={index * 160}>
      <figure
        data-testid={`testimonial-pullquote-${review.id}`}
        className={`relative max-w-[900px] ${align} px-2`}
      >
        {/* Massive editorial pull-quote — hanging opening quote */}
        <div className="relative">
          <span
            aria-hidden="true"
            className="absolute font-magazine text-terra select-none leading-none"
            style={{
              fontSize: "clamp(5rem, 12vw, 10rem)",
              top: sideMode === 1 ? "-0.35em" : "-0.3em",
              left: sideMode === 1 ? "auto" : "-0.35em",
              right: sideMode === 1 ? "-0.15em" : "auto",
              opacity: 0.22,
            }}
          >
            &ldquo;
          </span>
          <blockquote
            className={`relative font-magazine font-normal text-sand-800 leading-[1.15]`}
            style={{ fontSize: "clamp(1.6rem, 3.4vw, 3rem)" }}
          >
            {review.text.length > 220
              ? review.text.slice(0, 218).trimEnd() + "…"
              : review.text}
          </blockquote>
        </div>

        {/* Author line — small circular avatar floating at end */}
        <figcaption
          className={`mt-8 flex items-center gap-4 ${sideMode === 1 ? "justify-end" : ""}`}
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-peach-100 text-terra font-magazine font-semibold text-base">
            {review.author.charAt(0)}
          </span>
          <div className={sideMode === 1 ? "text-right" : ""}>
            <div className="text-[13px] font-semibold text-sand-800">
              {review.author}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-sand-500 font-medium">
              <Stars size="h-3 w-3" />
              <span>· Google · {review.time}</span>
            </div>
          </div>
        </figcaption>
      </figure>
    </Reveal>
  );
}

export default function Testimonials() {
  const featured = FEATURED_IDS.map((id) =>
    GOOGLE_REVIEWS.find((r) => r.id === id)
  ).filter(Boolean);

  return (
    <section
      id="stimmen"
      className="relative bg-cream-100 py-32 md:py-44 overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial section header · asymmetric */}
        <Reveal>
          <div className="grid md:grid-cols-12 gap-6 items-end mb-20">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                  Aus dem Kurs · Echte Google-Stimmen
                </span>
              </div>
              <h2 className="font-magazine font-medium text-sand-800 leading-[1.08] text-[2.8rem] sm:text-[3.8rem] lg:text-[4.6rem]">
                Was Absolvent:innen <br />
                <span className="text-terra italic underline decoration-saffron-dark decoration-[3px] underline-offset-[10px]">wirklich sagen.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:ml-auto md:text-right">
              <a
                href={GOOGLE_PROFILE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="google-rating-badge"
                className="inline-flex items-center gap-4 group"
              >
                <GoogleG className="h-9 w-9" />
                <div className="leading-tight text-left">
                  <div className="flex items-center gap-2">
                    <span className="font-magazine text-[1.75rem] font-normal text-sand-800">
                      4,8
                    </span>
                    <Stars size="h-4 w-4" />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-sand-500 mt-1 font-semibold">
                    183 Google-Rezensionen
                  </div>
                </div>
                <ExternalLink
                  className="h-4 w-4 text-sand-400 group-hover:text-terra group-hover:translate-x-1 transition-all"
                  strokeWidth={1.6}
                />
              </a>
            </div>
          </div>
        </Reveal>

        {/* Pull-quotes — massive Serif, staggered alignment, chapter-like rhythm */}
        <div className="space-y-24 md:space-y-32">
          {featured.map((r, idx) => (
            <PullQuote key={r.id} review={r} index={idx} sideMode={idx % 3} />
          ))}
        </div>

        {/* Echte Abschlussmomente — Fotostrecke */}
        <Reveal delay={300}>
          <div className="mt-28 md:mt-36" data-testid="testimonial-photo-strip">
            <div className="flex items-center gap-3 mb-10">
              <span className="h-px w-10 bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                Echte Abschlussmomente · Unsere Absolvent:innen
              </span>
            </div>
            <div className="flex flex-wrap items-start justify-center lg:justify-between gap-x-5 gap-y-8">
              {[
                { src: "/images/erfolge/abschluss-duo.webp", alt: "Drei Absolventinnen feiern ihre Zertifikate", caption: "Geschafft · Zertifikat in der Tasche", portrait: true },
                { src: "/images/erfolge/abschluss-gruppe.webp", alt: "Absolventen mit Zertifikat Pflegeassistenz nach LG1 und LG2", caption: "Pflegeassistenz nach LG1 & LG2 · bestanden", portrait: false },
                { src: "/images/erfolge/abschluss-trio-seminar.webp", alt: "Kursgruppe zeigt stolz ihre Zertifikate", caption: "Zertifikatsübergabe im Campus", portrait: false },
                { src: "/images/erfolge/abschluss-trio-lounge.webp", alt: "Zwei Absolventinnen mit Zertifikat", caption: "Stolz verdient · Abschlussmoment", portrait: true },
              ].map((f) => (
                <figure key={f.src} className={f.portrait ? "w-[46%] sm:w-[30%] lg:w-[17%]" : "w-full sm:w-[62%] lg:w-[30%]"}>
                  <img
                    src={f.src}
                    alt={f.alt}
                    loading="lazy"
                    className={`w-full ${f.portrait ? "aspect-[3/4]" : "aspect-[4/3]"} object-cover rounded-2xl border border-cream-300 shadow-soft`}
                  />
                  <figcaption className="mt-3 text-[11px] uppercase tracking-[0.18em] text-sand-500 font-semibold leading-relaxed">
                    {f.caption}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Editorial footer under the pull-quotes */}
        <Reveal delay={520}>
          <div className="mt-24 pt-10 border-t border-sand-300/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-[12.5px] text-sand-600 max-w-2xl leading-relaxed">
              Alle Zitate stammen unverändert vom öffentlichen Google-Unternehmensprofil
              — kein Cherry-Picking, keine gecodeten Testimonials.
            </p>
            <a
              href={GOOGLE_PROFILE_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="google-reviews-link"
              className="inline-flex items-center gap-2 text-[13px] font-semibold text-sand-800 border-b border-terra pb-0.5 hover:text-terra transition-colors whitespace-nowrap"
            >
              Alle 183 Rezensionen ansehen
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
